/**
 * API Client untuk aplikasi PAK MoO
 * Menggunakan fetch API dengan konfigurasi dari environment variables
 */

import { getApiUrl, API_TIMEOUT } from '../config/api';

/**
 * Helper untuk mendapatkan token dari localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('auth_token') || localStorage.getItem('access_token');
};

/**
 * Helper untuk menyimpan token ke localStorage
 */
export const setAuthToken = (token) => {
  localStorage.setItem('auth_token', token);
  localStorage.setItem('access_token', token);
};

/**
 * Helper untuk menghapus token dari localStorage
 */
export const removeAuthToken = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

/**
 * Helper untuk mendapatkan headers default
 */
const getDefaultHeaders = (customHeaders = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };
  
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

/**
 * Helper untuk membuat request dengan timeout
 */
const fetchWithTimeout = (url, options, timeout = API_TIMEOUT) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeout)
    ),
  ]);
};

/**
 * Helper untuk handle response
 */
const handleResponse = async (response) => {
  // Jika response OK, parse JSON
  if (response.ok) {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    return await response.text();
  }
  
  // Handle error responses
  let errorData;
  try {
    errorData = await response.json();
  } catch {
    errorData = { message: response.statusText || 'Terjadi kesalahan' };
  }
  
  // Jika unauthorized, hapus token dan redirect ke login
  if (response.status === 401) {
    removeAuthToken();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  
  const error = new Error(errorData.message || 'Terjadi kesalahan');
  error.status = response.status;
  error.data = errorData;
  throw error;
};

/**
 * GET request
 */
export const apiGet = async (endpoint, options = {}) => {
  try {
    const url = getApiUrl(endpoint);
    const response = await fetchWithTimeout(
      url,
      {
        method: 'GET',
        headers: getDefaultHeaders(options.headers),
        ...options,
      },
      options.timeout || API_TIMEOUT
    );
    
    return await handleResponse(response);
  } catch (error) {
    console.error('API GET Error:', error);
    throw error;
  }
};

/**
 * POST request
 */
export const apiPost = async (endpoint, data = {}, options = {}) => {
  try {
    const url = getApiUrl(endpoint);
    const response = await fetchWithTimeout(
      url,
      {
        method: 'POST',
        headers: getDefaultHeaders(options.headers),
        body: JSON.stringify(data),
        ...options,
      },
      options.timeout || API_TIMEOUT
    );
    
    return await handleResponse(response);
  } catch (error) {
    console.error('API POST Error:', error);
    throw error;
  }
};

/**
 * PUT request
 */
export const apiPut = async (endpoint, data = {}, options = {}) => {
  try {
    const url = getApiUrl(endpoint);
    const response = await fetchWithTimeout(
      url,
      {
        method: 'PUT',
        headers: getDefaultHeaders(options.headers),
        body: JSON.stringify(data),
        ...options,
      },
      options.timeout || API_TIMEOUT
    );
    
    return await handleResponse(response);
  } catch (error) {
    console.error('API PUT Error:', error);
    throw error;
  }
};

/**
 * PATCH request
 */
export const apiPatch = async (endpoint, data = {}, options = {}) => {
  try {
    const url = getApiUrl(endpoint);
    const response = await fetchWithTimeout(
      url,
      {
        method: 'PATCH',
        headers: getDefaultHeaders(options.headers),
        body: JSON.stringify(data),
        ...options,
      },
      options.timeout || API_TIMEOUT
    );
    
    return await handleResponse(response);
  } catch (error) {
    console.error('API PATCH Error:', error);
    throw error;
  }
};

/**
 * DELETE request
 */
export const apiDelete = async (endpoint, options = {}) => {
  try {
    const url = getApiUrl(endpoint);
    const response = await fetchWithTimeout(
      url,
      {
        method: 'DELETE',
        headers: getDefaultHeaders(options.headers),
        ...options,
      },
      options.timeout || API_TIMEOUT
    );
    
    return await handleResponse(response);
  } catch (error) {
    console.error('API DELETE Error:', error);
    throw error;
  }
};

/**
 * Upload file
 */
export const apiUpload = async (endpoint, file, options = {}) => {
  try {
    const url = getApiUrl(endpoint);
    const formData = new FormData();
    formData.append('file', file);
    
    // Hapus Content-Type header untuk FormData (browser akan set otomatis)
    const headers = getDefaultHeaders(options.headers);
    delete headers['Content-Type'];
    
    const response = await fetchWithTimeout(
      url,
      {
        method: 'POST',
        headers: {
          ...headers,
          ...options.headers,
        },
        body: formData,
        ...options,
      },
      options.timeout || API_TIMEOUT
    );
    
    return await handleResponse(response);
  } catch (error) {
    console.error('API Upload Error:', error);
    throw error;
  }
};

// Export default object
export default {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  patch: apiPatch,
  delete: apiDelete,
  upload: apiUpload,
  setAuthToken,
  removeAuthToken,
};

